import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LocalizedDateOptions,
  formatLocalizedDate,
  formatLocalizedDateTime,
  getLocalizedDefaultDate,
  getRelativeTimeString
} from '../utils/time-util';

/**
 * React hook for localized date formatting
 * Integrates with the existing i18n system
 */
export const useLocalizedDate = () => {
  const { i18n } = useTranslation();
  
  const currentLocale = useMemo(() => {
    return i18n.language || 'en';
  }, [i18n.language]);

  /**
   * Formats a date using the current locale
   */
  const formatDate = useMemo(() => {
    return (date: Date, options?: Omit<LocalizedDateOptions, 'locale'>) => {
      return formatLocalizedDate(date, { 
        locale: currentLocale, 
        ...options 
      });
    };
  }, [currentLocale]);

  /**
   * Gets the current date formatted for the current locale
   */
  const getDefaultDate = useMemo(() => {
    return (options?: Omit<LocalizedDateOptions, 'locale'>) => {
      return getLocalizedDefaultDate({ 
        locale: currentLocale, 
        ...options 
      });
    };
  }, [currentLocale]);

  /**
   * Formats a date with time using the current locale
   */
  const formatDateTime = useMemo(() => {
    return (date: Date, options?: Omit<LocalizedDateOptions, 'locale'>) => {
      return formatLocalizedDateTime(date, { 
        locale: currentLocale, 
        ...options 
      });
    };
  }, [currentLocale]);

  /**
   * Gets relative time string for the current locale
   */
  const getRelativeTime = useMemo(() => {
    return (date: Date) => {
      return getRelativeTimeString(date, currentLocale);
    };
  }, [currentLocale]);

  return {
    formatDate,
    getDefaultDate,
    formatDateTime,
    getRelativeTime,
    currentLocale
  };
}; 
